import React, { useState } from "react";
import { Loader2, AlertTriangle, PlayCircle, StopCircle } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Switch } from "@/components/ui/switch";
import { toast } from "sonner";
import { api } from "@/lib/api";
import { useSite } from "@/context/SiteContext";

function toLocalInput(iso) {
  if (!iso) return "";
  const d = new Date(iso);
  const off = d.getTimezoneOffset();
  const local = new Date(d.getTime() - off * 60000);
  return local.toISOString().slice(0, 16);
}

export default function CampaignTab() {
  const { settings, refresh } = useSite();
  const [form, setForm] = useState({
    campaign_title: settings?.campaign_title || "",
    campaign_active: settings?.campaign_active ?? true,
    campaign_ended_manual: settings?.campaign_ended_manual ?? false,
    campaign_end_at: toLocalInput(settings?.campaign_end_at),
  });
  const [saving, setSaving] = useState(false);

  const save = async () => {
    setSaving(true);
    try {
      const payload = {
        campaign_title: form.campaign_title,
        campaign_active: form.campaign_active,
        campaign_ended_manual: form.campaign_ended_manual,
        campaign_end_at: form.campaign_end_at ? new Date(form.campaign_end_at).toISOString() : null,
      };
      await api.put("/settings", payload);
      await refresh();
      toast.success("Campanha atualizada!");
    } catch { toast.error("Falha ao salvar"); }
    finally { setSaving(false); }
  };

  const open = settings?.campaign_open;

  return (
    <div className="max-w-xl space-y-6">
      <div className={`card-surface p-5 flex items-center gap-3 ${open ? "" : ""}`} data-testid="campaign-status">
        {open ? <PlayCircle className="text-green-400" /> : <StopCircle className="text-red-400" />}
        <div>
          <div className="font-bold">{open ? "Vendas abertas" : "Vendas encerradas"}</div>
          <div className="text-sm text-[var(--text-muted)]">{open ? "A loja do evento está recebendo pedidos." : "A loja mostra 'Vendas encerradas' e bloqueia compras (inclusive links diretos)."}</div>
        </div>
      </div>

      <div className="card-surface p-6 space-y-5">
        <div><Label>Nome da campanha</Label><Input value={form.campaign_title} onChange={(e) => setForm({ ...form, campaign_title: e.target.value })} className="bg-[var(--bg-secondary)]" data-testid="campaign-title" /></div>

        <div>
          <Label>Data e horário de encerramento (automático)</Label>
          <Input type="datetime-local" value={form.campaign_end_at} max="2026-12-31T23:59"
            onChange={(e) => setForm({ ...form, campaign_end_at: e.target.value })} className="bg-[var(--bg-secondary)]" data-testid="campaign-end-at" />
          <p className="text-xs text-[var(--text-muted)] mt-1">A loja encerra automaticamente ao atingir essa data. Deixe vazio para não agendar.</p>
        </div>

        <label className="flex items-center justify-between gap-2 border-t border-[var(--border)] pt-4">
          <span><b>Campanha ativa</b><br /><span className="text-xs text-[var(--text-muted)]">Desmarque para ocultar a loja por completo</span></span>
          <Switch checked={form.campaign_active} onCheckedChange={(v) => setForm({ ...form, campaign_active: v })} data-testid="campaign-active-switch" />
        </label>

        <label className="flex items-center justify-between gap-2">
          <span className="flex items-center gap-2"><AlertTriangle size={16} className="text-red-400" /><span><b>Encerrar manualmente agora</b><br /><span className="text-xs text-[var(--text-muted)]">Bloqueia compras imediatamente</span></span></span>
          <Switch checked={form.campaign_ended_manual} onCheckedChange={(v) => setForm({ ...form, campaign_ended_manual: v })} data-testid="campaign-manual-switch" />
        </label>

        <Button onClick={save} disabled={saving} data-testid="campaign-save" className="btn-primary font-bold w-full h-11">
          {saving ? <Loader2 className="animate-spin mr-2" size={18} /> : null} Salvar campanha
        </Button>
      </div>
    </div>
  );
}
